import { AwsComponentKey } from "@p0security/shared/integrations/resources/aws/types";
import { AzureComponentKey } from "@p0security/shared/integrations/resources/azure/types";
import { GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG } from "@p0security/shared/integrations/resources/gcloud/constants";
import {
  GcloudIamComponentKey,
  GcloudSecurityPerimeterComponentKey,
} from "@p0security/shared/integrations/resources/gcloud/types";
import { NEW_SENTINEL } from "components/Integrations/Install/Component";
import { PermissionIntegrationLogo } from "components/Integrations/IntegrationLogo";
import { DOCS_URLS } from "constants/docsUrls";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback } from "react";
import { useParams } from "react-router-dom";

import { useNavigateWithSearchParams } from "../../Assessment/hooks/useNavigateWithSearchParams";
import { InstallPromptStep } from "../components/Prompt";
import { WelcomePaths } from "../paths";
import { SupportedCloudProviders, WelcomeFlows } from "../types";

const ChooseProduct = React.memo(() => {
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const { orgSlug, cloudProvider } = useParams<{
    orgSlug: string;
    cloudProvider: SupportedCloudProviders;
  }>();

  const flags = useFlags();

  const navigateToJIT = useCallback(() => {
    const isSecurityPerimeterEnabled =
      flags[GCLOUD_SECURITY_PERIMETER_FEATURE_FLAG];

    const integration:
      | AwsComponentKey
      | AzureComponentKey
      | GcloudIamComponentKey
      | GcloudSecurityPerimeterComponentKey
      | undefined =
      cloudProvider === "aws"
        ? "iam-write"
        : cloudProvider === "azure"
          ? "azure-app"
          : cloudProvider === "gcloud"
            ? isSecurityPerimeterEnabled
              ? "iam-write-security-perimeter"
              : "iam-write"
            : "iam-write";

    if (integration) {
      navigateWithSearchParams(
        `/o/${orgSlug}/${WelcomePaths.Root}/${WelcomePaths.Onboarding}/${cloudProvider}/${integration}/${NEW_SENTINEL}?flow=${WelcomeFlows.JIT}`
      );
    } else {
      console.warn(`No integration found for cloud provider ${cloudProvider}.`);
    }
  }, [flags, cloudProvider, navigateWithSearchParams, orgSlug]);

  const navigateToAssessment = useCallback(() => {
    navigateWithSearchParams(
      `/o/${orgSlug}/${WelcomePaths.Root}/${WelcomePaths.Onboarding}/${cloudProvider}/iam-assessment/_new?flow=${WelcomeFlows.ASSESSMENT}`
    );
  }, [navigateWithSearchParams, orgSlug, cloudProvider]);

  const openJITDocs = useCallback(() => {
    window.open(DOCS_URLS.JIT_BASE, "_blank");
  }, []);

  const openAssessmentDocs = useCallback(() => {
    window.open(DOCS_URLS.BASE, "_blank");
  }, []);

  return (
    <InstallPromptStep
      prompts={[
        {
          title: "Access Orchestration",
          features: [
            "Give users access to perform specific tasks for just as long as they need it.",
            "Automatically revoke access when the task is complete.",
            "Grant access more quickly and with less friction.",
          ],
          onPrimary: {
            text: "Get Started",
            shortText: "Start",
            onClick: navigateToJIT,
          },
          onSecondary: {
            text: "Read the Docs",
            shortText: "Docs",
            onClick: openJITDocs,
          },
        },
        {
          title: "Privilege Governance",
          features: [
            "List identities, entitlements, and resources in all of your cloud environments.",
            "Discover access vulnerabilities and risks.",
            "Automatically remediate issues as they are found.",
            "Track identities across environments to gain end-to-end visibility into your systems.",
          ],
          disabled: cloudProvider === "azure",
          disabledText: (
            <span>{PermissionIntegrationLogo["azure"]} Coming soon</span>
          ),
          onPrimary: {
            text: "Get Started",
            shortText: "Start",
            onClick: navigateToAssessment,
          },
          onSecondary: {
            text: "Read the Docs",
            shortText: "Docs",
            onClick: openAssessmentDocs,
          },
        },
      ]}
    />
  );
});

ChooseProduct.displayName = "ChooseProduct";

export { ChooseProduct };
